import { GoalRequired } from '@/components/GoalRequired';
import { useEffect, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { api, localDate } from '@/lib/api';
import { useAuth } from '@/lib/auth';
import { ScoreRing, colorOf } from '@/components/ScoreRing';
import { RegenLogo } from '@/components/RegenLogo';
import { NotificationBell } from '@/components/NotificationBell';
import { LineChart, Line, ResponsiveContainer, XAxis, YAxis, Tooltip } from 'recharts';
import { Dumbbell, UtensilsCrossed, Heart, Brain, Moon, Timer, Utensils, Footprints, Scale, MessageSquare, ChevronRight, ArrowRight, Lightbulb, Trophy, Target, CheckCircle2, Circle, Sparkles } from 'lucide-react';
import { useProgramLocked } from '@/components/ProgramLock';
import PreparationHome from './PreparationHome';
import { DailyMovement } from '@/components/DailyMovement';
import { fmtDay } from '@/lib/dates';

const TRAINING_IMG = 'https://images.pexels.com/photos/4720759/pexels-photo-4720759.jpeg?auto=compress&cs=tinysrgb&dpr=2&h=400&w=600';
const NUTRITION_IMG = 'https://images.pexels.com/photos/1247677/pexels-photo-1247677.jpeg?auto=compress&cs=tinysrgb&dpr=2&h=400&w=600';
const COACH_IMG = 'https://customer-assets.emergentagent.com/job_d735c4ed-ebb5-4daa-8cc3-5efce7f3e601/artifacts/j9lk0uwm_Screenshot%202026-06-21%20at%206.49.22%E2%80%AFPM.png';

export default function Home() {
  const { user } = useAuth();
  const locked = useProgramLocked();
  const nav = useNavigate();
  const tier = user?.subscription_tier || 'regen_method';
  const isTracker = tier === 'tracker';
  const hasPlus = tier === 'regen_plus' || tier === 'regen_method';
  const isMethod = tier === 'regen_method';
  const [score, setScore] = useState(null);
  const [snap, setSnap] = useState(null);
  const [insight, setInsight] = useState(null);
  const [unread, setUnread] = useState(0);
  const [training, setTraining] = useState(null);
  const [nutrition, setNutrition] = useState({ planned: false, remaining: 0 });
  const [calTarget, setCalTarget] = useState(null);
  const [summary, setSummary] = useState(null);
  const [programs, setPrograms] = useState([]);
  const [agenda, setAgenda] = useState(null);

  useEffect(() => {
    if (locked) return;
    api.get('/regen-score').then(r => setScore(r.data));
    api.get('/health-snapshot').then(r => setSnap(r.data));
    api.get('/coach-insight').then(r => setInsight(r.data)).catch(() => {});
    api.get('/progress/summary').then(r => setSummary(r.data)).catch(() => {});
    api.get('/messages/unread/count').then(r => setUnread(r.data.count)).catch(() => {});
    api.get('/my-programs').then(r => setPrograms(r.data || [])).catch(() => {});
    api.get('/today-agenda').then(r => setAgenda(r.data)).catch(() => {});
    const today = localDate();
    api.get('/workouts/assignments', { params: { date_from: today, date_to: today } }).then(r => setTraining(r.data[0] || null));
    Promise.all([
      api.get('/meal-plans/assignments', { params: { date_from: today, date_to: today } }),
      api.get('/nutrition/daily-summary', { params: { date: today } }),
    ]).then(([mp, sum]) => {
      const total = mp.data.length;
      const done = mp.data.filter(a => a.followed).length;
      setNutrition({ planned: total > 0, remaining: Math.max(0, total - done), total });
      setCalTarget(sum.data?.target?.calories || null);
    });
  }, []);

  const tProg = programs.find(p => p.program_type === 'training');
  const nProg = programs.find(p => p.program_type === 'nutrition');
  const rProg = programs.find(p => p.program_type === 'recovery');

  const hour = new Date().getHours();
  const greeting = hour < 12 ? 'Good morning' : hour < 18 ? 'Good afternoon' : 'Good evening';
  const startW = user?.starting_weight_kg, curW = snap?.weight?.value ?? user?.current_weight_kg, goalW = user?.goal_weight_kg;
  const goalType = user?.goal_type || '';
  // Remaining toward goal, based on CURRENT → GOAL (clearer than past-tense lost/gained)
  const remainingKg = (curW != null && goalW != null) ? Math.round((goalW - curW) * 10) / 10 : null;
  let changeLabel = 'Remaining', changeValue = '—';
  if (remainingKg != null) {
    if (remainingKg > 0.0) changeValue = `To Gain ${Math.abs(remainingKg)} kg`;
    else if (remainingKg < 0.0) changeValue = `To Lose ${Math.abs(remainingKg)} kg`;
    else changeValue = 'Goal reached';
  }
  const denom = (startW != null && goalW != null) ? (goalW - startW) : 0;
  const progressPct = (startW != null && goalW != null && curW != null && denom !== 0)
    ? Math.max(0, Math.min(100, Math.round(((curW - startW) / denom) * 100)))
    : ((startW != null && goalW != null && startW === goalW) ? 100 : 0);

  const pillars = [
    { label: 'Training', val: score?.training ?? 0, icon: Dumbbell, color: '#00C8FF', to: '/app/training' },
    { label: 'Nutrition', val: score?.nutrition ?? 0, icon: UtensilsCrossed, color: '#65A30D', to: '/app/nutrition' },
    { label: 'Recovery', val: score?.recovery ?? 0, icon: Heart, color: '#A78BFA', to: '/app/recovery' },
    { label: 'Mindset', val: score?.mindset ?? 0, icon: Brain, color: '#FFB800', to: '/app/mindset' },
  ];

  const statusFor = (v) => v >= 90 ? 'Excellent' : v >= 86 ? 'Great' : v >= 70 ? 'Good' : v >= 50 ? 'Fair' : 'Low';

  const health = [
    { key: 'sleep', label: 'Sleep', icon: Moon, color: '#A78BFA', value: snap?.sleep?.value || '—', sub: snap?.sleep?.label, scolor: colorOf(snap?.sleep?.color), to: '/app/sleep' },
    { key: 'lastmeal', label: 'Last Meal', icon: Utensils, color: '#FFB800', value: snap?.last_meal?.value || '—', sub: snap?.last_meal?.label ? `${snap.last_meal.label} Meal Timing` : 'Not logged', scolor: colorOf(snap?.last_meal?.color), to: '/app/fasting', prominent: true },
    { key: 'fasting', label: 'Fasting', icon: Timer, color: '#00C8FF', value: snap?.fasting?.value || '—', sub: snap?.fasting?.label, scolor: colorOf(snap?.fasting?.color), to: '/app/fasting' },
    { key: 'weight', label: 'Weight', icon: Scale, color: '#00C8FF', value: snap?.weight?.value != null ? `${snap.weight.value} kg` : '—', sub: snap?.weight?.change ? `${snap.weight.change > 0 ? '+' : ''}${snap.weight.change} kg` : 'Stable', scolor: '#00C8FF', to: '/app/progress' },
    { key: 'steps', label: 'Daily Movement', icon: Footprints, color: '#65A30D', value: snap?.steps?.value != null ? Number(snap.steps.value).toLocaleString() : '—', sub: 'Steps today', scolor: '#65A30D', to: '/app/steps' },
  ];

  return (
    <div className="space-y-5 page-grid" data-testid="client-home">
      <GoalRequired />
      {locked && <PreparationHome />}
      {locked ? null : (<>
      {/* Header */}
      <div className="flex items-center justify-between">
        <div className="min-w-0">
          <h1 className="font-display font-black text-2xl leading-tight truncate">{greeting}, {user?.name?.split(' ')[0] || 'Athlete'} 👋</h1>
          <div className="text-xs text-muted-foreground mt-0.5">Discipline today. Transformation forever.</div>
        </div>
        <div className="flex items-center gap-2 flex-shrink-0">
          <span className="hidden sm:inline-flex"><RegenLogo size="sm" /></span>
          <NotificationBell />
          <button onClick={() => nav('/app/coach')} className="w-10 h-10 rounded-full bg-[var(--c-film)] hover:bg-[var(--c-film)] flex items-center justify-center relative" data-testid="notifications-btn">
            <MessageSquare className="w-4 h-4" />
            {unread > 0 && <span className="absolute -top-1 -right-1 min-w-[18px] h-[18px] px-1 rounded-full bg-[#00C8FF] text-[#081726] text-[10px] font-bold flex items-center justify-center">{unread}</span>}
          </button>
        </div>
      </div>

      {/* Programme not started yet (coach-selected future start date) */}
      {(tProg || nProg) && (tProg || nProg).start_date > localDate() && (
        <div className="regen-card-elevated p-5 border border-[#00C8FF]/30" data-testid="home-programme-starts">
          <div className="label-mini text-[#00C8FF]">Programme Approved</div>
          <div className="font-display font-black text-xl mt-1" data-testid="home-programme-starts-date">Your programme starts on {fmtDay((tProg || nProg).start_date, { weekday: 'long', day: 'numeric', month: 'long' })}.</div>
          <p className="text-sm text-muted-foreground mt-1">Use today to plan your meals and sessions. Week 1 begins on your start date.</p>
        </div>
      )}

      {/* SECTION 0: Today's Agenda (YourReGen Protocol only) */}
      {isMethod && agenda && <div className={insight ? "lg-half lg-stretch" : ""}><TodayAgenda agenda={agenda} /></div>}

      {/* Coach JP Analysis — YourReGen Protocol only */}
      {isMethod && insight && (
        <button onClick={() => nav('/app/coach-analysis')} data-testid="coach-insight-card" className="lg-half lg-stretch w-full text-left regen-card-elevated p-5 active:scale-[0.99] transition relative overflow-hidden">
          <div className="absolute -right-6 -top-6 w-28 h-28 rounded-full bg-[#A78BFA]/10 blur-2xl" />
          <div className="relative flex items-start gap-3">
            <img src={COACH_IMG} alt="Coach" className="w-12 h-12 rounded-xl object-cover ring-2 ring-[#A78BFA]/40 flex-shrink-0 bg-[var(--c-elev)]" />
            <div className="flex-1 min-w-0">
              <div className="flex items-center justify-between mb-1">
                <div className="flex items-center gap-1.5">
                  <Lightbulb className="w-3.5 h-3.5 text-[#A78BFA]" />
                  <span className="text-sm font-display font-bold text-[#A78BFA]">Coach JP Analysis</span>
                </div>
                <div className="flex items-center gap-1 text-[#A78BFA] text-xs font-semibold">View <ChevronRight className="w-3.5 h-3.5" /></div>
              </div>
              <div className="text-sm leading-snug text-foreground/90">{insight.insight}</div>
              {insight.focus && (
                <div className="mt-2.5 flex items-center gap-2 rounded-xl bg-[var(--c-green-soft)] px-3 py-2" data-testid="todays-focus">
                  <Target className="w-4 h-4 text-[var(--c-green-ink)] flex-shrink-0" />
                  <div><span className="text-[10px] uppercase tracking-wider text-[var(--c-green-ink)] font-bold">Today's Focus</span><div className="text-sm font-semibold">{insight.focus}</div></div>
                </div>
              )}
            </div>
          </div>
        </button>
      )}

      {/* SECTION 1: Today's Plan (Training · Nutrition · Recovery) — YourReGen+ & Method */}
      {hasPlus && (
      <div className="grid grid-cols-1 sm:grid-cols-3 gap-3">
        <ProgramCard
          testid="today-training-card" tag="Training" tagColor="#00C8FF" icon={Dumbbell}
          title="Training"
          todayLabel="Today's session"
          todayText={agenda?.training?.length
            ? agenda.training.map(t => t.name).join(', ')
            : (training?.workout_snapshot?.name || 'Rest day')}
          image={TRAINING_IMG} cta="Open Training" ctaBg="#00C8FF" onClick={() => nav('/app/training')}
        />
        <ProgramCard
          testid="today-nutrition-card" tag="Nutrition" tagColor='#65A30D' icon={UtensilsCrossed}
          title="Nutrition"
          todayLabel="Today's target"
          todayText={calTarget ? `${calTarget.toLocaleString()} kcal` : (nutrition.planned ? `${nutrition.remaining} meal${nutrition.remaining === 1 ? '' : 's'} remaining` : 'Build your own meal')}
          image={NUTRITION_IMG} cta="Open Nutrition" ctaBg='#65A30D' onClick={() => nav('/app/nutrition')}
        />
        <ProgramCard
          testid="today-recovery-card" tag="Recovery" tagColor="#A78BFA" icon={Heart}
          title="Recovery"
          todayLabel="Today's focus"
          todayText={agenda?.recovery?.length ? agenda.recovery.map(r => r.name).join(', ') : 'Self-log your recovery'}
          image={null} cta="Open Recovery" ctaBg="#A78BFA" onClick={() => nav('/app/recovery')}
        />
      </div>
      )}

      {/* Tracker Quick Log */}
      {isTracker && <QuickLog nav={nav} />}

      {/* SECTION 2: Weekly Report — all tiers */}
      {(
      <button onClick={() => nav('/app/digest')} data-testid="weekly-digest-card" className="lg-half lg-stretch w-full text-left regen-card-elevated p-4 active:scale-[0.99] transition flex items-center gap-3">
        <div className="w-10 h-10 rounded-xl bg-[var(--c-green-soft)] flex items-center justify-center flex-shrink-0">
          <Trophy className="w-5 h-5 text-[var(--c-green-ink)]" />
        </div>
        <div className="flex-1 min-w-0">
          <div className="text-sm font-display font-bold">Weekly Report</div>
          <div className="text-xs text-muted-foreground">Your Training, Nutrition & Recovery scorecard</div>
        </div>
        <ChevronRight className="w-4 h-4 text-[var(--c-green-ink)]" />
      </button>
      )}

      {/* SECTION 2b: YourReGen Score — YourReGen+ & Method */}
      {hasPlus && score && score.ready === false && (
        <ScoreUnlockCard readiness={score.readiness} />
      )}
      {hasPlus && score && score.ready !== false && (
      <button onClick={() => nav('/app/score')} data-testid="regen-score-card" className="lg-half lg-stretch w-full text-left regen-card-elevated p-5 blueprint-grid active:scale-[0.99] transition">
        <div className="flex items-center justify-between mb-2">
          <div className="label-mini text-[var(--c-green-ink)]">YourReGen Score</div>
          <div className="flex items-center gap-1 text-[#00C8FF] text-xs font-semibold">View Details <ChevronRight className="w-3.5 h-3.5" /></div>
        </div>
        <div className="flex items-center gap-4">
          <ScoreRing value={score?.score ?? 0} color='#65A30D' size={120} stroke={11} sub={score?.level} />
          <div className="flex-1 min-w-0">
            <div className="h-24">
              <ResponsiveContainer width="100%" height="100%">
                <LineChart data={score?.series || []} margin={{ top: 8, right: 6, left: -24, bottom: 0 }}>
                  <XAxis dataKey="dow" tick={{ fontSize: 10, fill: '#7E8C9E' }} axisLine={false} tickLine={false} />
                  <YAxis domain={[40, 100]} tick={{ fontSize: 9, fill: '#7E8C9E' }} axisLine={false} tickLine={false} width={28} />
                  <Tooltip contentStyle={{ background: '#101E30', border: '1px solid rgba(255,255,255,0.08)', borderRadius: 10, fontSize: 12 }} formatter={(v) => [v, 'Score']} />
                  <Line type="monotone" dataKey="score" stroke='#65A30D' strokeWidth={2.5} dot={{ r: 3, fill: '#65A30D' }} activeDot={{ r: 5 }} />
                </LineChart>
              </ResponsiveContainer>
            </div>
            <div className="text-right mt-1">
              <span className={`font-mono text-lg font-bold ${(score?.weekly_trend ?? 0) >= 0 ? 'text-[var(--c-green-ink)]' : 'text-[#FF5C5C]'}`} data-testid="score-trend">
                {(score?.weekly_trend ?? 0) >= 0 ? '↑ +' : '↓ '}{score?.weekly_trend ?? 0}
              </span>
              <span className="text-xs text-muted-foreground ml-1">this week</span>
            </div>
          </div>
        </div>
      </button>
      )}

      {/* SECTION 3: Pillar Scores — YourReGen+ & Method */}
      {hasPlus && score && score.ready !== false && (
      <div>
        <div className="flex items-center justify-between mb-2">
          <div className="label-mini">Pillar Scores</div>
          <button onClick={() => nav('/app/score')} className="text-xs text-[#00C8FF] font-semibold flex items-center gap-1">View All <ChevronRight className="w-3 h-3" /></button>
        </div>
        <div className="grid grid-cols-2 sm:grid-cols-4 gap-3">
          {pillars.map(p => (
            <button key={p.label} onClick={() => nav(p.to)} data-testid={`pillar-${p.label.toLowerCase()}`} className="regen-card-elevated p-3 text-left active:scale-[0.98] transition">
              <div className="flex items-center gap-1.5">
                <p.icon className="w-4 h-4" style={{ color: p.color }} />
                <span className="text-xs text-muted-foreground">{p.label}</span>
              </div>
              <div className="font-mono text-3xl mt-1.5">{p.val}</div>
              <div className="text-xs font-semibold mt-0.5" style={{ color: p.color }}>{statusFor(p.val)}</div>
            </button>
          ))}
        </div>
      </div>
      )}

      {/* SECTION 4: Today's Health Snapshot */}
      <div>
        <div className="label-mini mb-2">Today's Health Snapshot</div>
        <div className="grid grid-cols-2 gap-3">
          {health.filter(h => h.key !== 'steps').map((h, i) => (
            <button key={h.key} onClick={() => nav(h.to)} data-testid={`health-${h.key}`} className={`regen-card-elevated p-4 text-left active:scale-[0.98] transition ${i === 4 ? 'col-span-2' : ''} ${h.prominent ? 'ring-1' : ''}`} style={h.prominent ? { borderColor: `${h.scolor}66`, boxShadow: `0 0 0 1px ${h.scolor}33` } : {}}>
              <div className="flex items-center justify-between">
                <div className="flex items-center gap-2">
                  <h.icon className="w-4 h-4" style={{ color: h.color }} />
                  <span className="text-xs text-muted-foreground">{h.label}</span>
                </div>
                <ChevronRight className="w-3.5 h-3.5 text-muted-foreground" />
              </div>
              <div className="flex items-center gap-2 mt-2">
                {h.prominent && <span className="w-2.5 h-2.5 rounded-full flex-shrink-0" style={{ background: h.scolor, boxShadow: `0 0 8px ${h.scolor}` }} />}
                <div className="font-mono text-2xl">{h.value}</div>
              </div>
              {h.sub && <div className="text-xs font-semibold mt-0.5" style={{ color: h.scolor }}>{h.sub}</div>}
            </button>
          ))}
          <DailyMovement steps={snap?.steps?.value} brisk={snap?.steps?.brisk} briskGoal={snap?.steps?.brisk_target || undefined} />
        </div>
      </div>

      {/* SECTION 5: Body Transformation */}
      <button onClick={() => nav('/app/progress')} data-testid="body-transformation-card" className="w-full text-left regen-card-elevated p-5 active:scale-[0.99] transition">
        <div className="flex items-center justify-between mb-3">
          <div className="label-mini">Body Transformation{goalType ? ` · ${goalType}` : ''}</div>
          <div className="flex items-center gap-1 text-[#00C8FF] text-xs font-semibold">View Progress <ChevronRight className="w-3.5 h-3.5" /></div>
        </div>
        <div className="flex items-center gap-5">
          <ScoreRing value={progressPct} color='#65A30D' size={104} stroke={10}
            center={<div className="flex flex-col items-center"><div className="font-mono text-2xl font-bold text-[var(--c-green-ink)]">{progressPct}%</div><Trophy className="w-4 h-4 text-muted-foreground mt-0.5" /></div>} />
          <div className="flex-1 grid grid-cols-2 gap-x-4 gap-y-2 text-sm">
            <Row label="Start" value={`${startW ?? '—'} kg`} />
            <Row label="Current" value={`${curW ?? '—'} kg`} valColor="#00C8FF" />
            <Row label="Target" value={`${goalW ?? '—'} kg`} valColor='#65A30D' />
            <Row label={changeLabel} value={changeValue} valColor='#65A30D' />
            <Row label="Weekly" value={snap?.weight?.weekly_change != null ? `${snap.weight.weekly_change > 0 ? '+' : ''}${snap.weight.weekly_change} kg` : '—'} valColor="#FFB800" />
            <Row label="Waist" value={summary?.waist_lost ? `${summary.waist_lost > 0 ? '-' : '+'}${Math.abs(summary.waist_lost)} cm` : '—'} valColor="#00C8FF" />
          </div>
        </div>
      </button>

      {/* Upgrade card */}
      {!isMethod && <UpgradeCard tier={tier} nav={nav} />}

      {/* SECTION 6: Coach Insight moved to top (below Today's Agenda) */}
      </>)}
    </div>
  );
}

function UpgradeCard({ tier, nav }) {
  const toPlus = tier === 'tracker';
  const color = toPlus ? '#00C8FF' : '#65A30D';
  const title = toPlus ? 'Upgrade to YourReGen+' : 'Upgrade to the YourReGen Protocol';
  const sub = toPlus
    ? 'Unlock personalized workout & nutrition programs, exercise videos, YourReGen Score and premium analytics.'
    : 'Work directly with Coach JP — personalized coaching, weekly check-ins, program updates and full support.';
  const go = () => nav(toPlus ? '/choose-journey' : '/enroll/regen-method');
  return (
    <button onClick={go} data-testid="upgrade-card" className="w-full text-left rounded-2xl p-5 active:scale-[0.99] transition relative overflow-hidden"
            style={{ border: `1px solid ${color}55`, background: `${color}10` }}>
      <div className="absolute -right-6 -top-6 w-28 h-28 rounded-full blur-2xl" style={{ background: `${color}22` }} />
      <div className="relative flex items-center gap-3">
        <div className="w-11 h-11 rounded-xl flex items-center justify-center flex-shrink-0" style={{ background: `${color}22` }}>
          {toPlus ? <Sparkles className="w-5 h-5" style={{ color }} /> : <Trophy className="w-5 h-5" style={{ color }} />}
        </div>
        <div className="flex-1 min-w-0">
          <div className="font-display font-black text-lg" style={{ color }}>{title}</div>
          <div className="text-xs text-foreground/70 leading-snug">{sub}</div>
        </div>
        <ArrowRight className="w-5 h-5" style={{ color }} />
      </div>
    </button>
  );
}

function QuickLog({ nav }) {
  const items = [
    { label: 'Log Workout', icon: Dumbbell, color: '#00C8FF', to: '/app/training' },
    { label: 'Log Meal', icon: UtensilsCrossed, color: '#65A30D', to: '/app/nutrition' },
    { label: 'Log Recovery', icon: Heart, color: '#A78BFA', to: '/app/recovery' },
  ];
  return (
    <div data-testid="quick-log">
      <div className="label-mini mb-2">Quick Log</div>
      <div className="grid grid-cols-3 gap-3">
        {items.map(it => (
          <button key={it.label} onClick={() => nav(it.to)} data-testid={`quicklog-${it.label.split(' ')[1].toLowerCase()}`}
                  className="regen-card-elevated p-4 flex flex-col items-center gap-2 active:scale-[0.97] transition">
            <div className="w-10 h-10 rounded-xl flex items-center justify-center" style={{ background: `${it.color}1a` }}><it.icon className="w-5 h-5" style={{ color: it.color }} /></div>
            <span className="text-xs font-medium text-center">{it.label}</span>
          </button>
        ))}
      </div>
    </div>
  );
}

function Row({ label, value, valColor }) {
  return (
    <div className="flex items-center justify-between">
      <span className="text-muted-foreground">{label}</span>
      <span className="font-mono font-semibold" style={valColor ? { color: valColor } : {}}>{value}</span>
    </div>
  );
}

function ProgramCard({ testid, tag, tagColor, icon: Icon, title, subtitle, todayLabel = 'Today', todayText, image, cta, ctaBg, onClick }) {
  return (
    <button onClick={onClick} data-testid={testid} className="regen-card-elevated overflow-hidden text-left active:scale-[0.99] transition flex flex-col">
      <div className="p-4 pb-3 flex-1">
        <div className="flex items-center justify-between mb-2">
          <div className="flex items-center gap-1.5">
            <Icon className="w-4 h-4" style={{ color: tagColor }} />
            <span className="text-[10px] tracking-[0.18em] uppercase font-bold" style={{ color: tagColor }}>{tag}</span>
          </div>
        </div>
        <div className="flex items-center gap-3">
          <div className="flex-1 min-w-0">
            <div className="font-display font-bold text-base leading-tight truncate">{title}</div>
            {subtitle && <div className="text-xs text-muted-foreground mt-0.5">{subtitle}</div>}
            {todayText && <div className="text-[11px] mt-1.5 truncate"><span style={{ color: tagColor }}>{todayLabel}:</span> <span className="text-foreground/80">{todayText}</span></div>}
          </div>
          {image
            ? <img src={image} alt={tag} className="w-14 h-14 rounded-xl object-cover flex-shrink-0" />
            : <div className="w-14 h-14 rounded-xl flex items-center justify-center flex-shrink-0" style={{ background: `${tagColor}1a` }}><Icon className="w-6 h-6" style={{ color: tagColor }} /></div>}
        </div>
      </div>
      <div className="px-4 pb-4">
        <div className="w-full rounded-xl py-2.5 flex items-center justify-center gap-2 font-semibold text-sm text-[#081726]" style={{ background: ctaBg }}>
          {cta} <ArrowRight className="w-4 h-4" />
        </div>
      </div>
    </button>
  );
}

function TodayAgenda({ agenda }) {
  const groups = [
    { key: 'training', label: 'Training', color: '#00C8FF', items: agenda.training },
    { key: 'nutrition', label: 'Nutrition', color: '#65A30D', items: agenda.nutrition },
    { key: 'recovery', label: 'Recovery', color: '#A78BFA', items: agenda.recovery },
    { key: 'coach', label: 'Coach Task', color: '#FFB800', items: agenda.coach_tasks },
  ].filter(g => g.items && g.items.length);
  return (
    <div className="regen-card-elevated p-5" data-testid="today-agenda">
      <div className="flex items-center justify-between mb-3">
        <div className="label-mini text-[var(--c-green-ink)]">Today's Agenda</div>
        <div className="font-mono text-sm font-bold" data-testid="agenda-progress">
          <span className="text-[var(--c-green-ink)]">{agenda.done}</span>
          <span className="text-muted-foreground"> / {agenda.total} done</span>
        </div>
      </div>
      <div className="h-1.5 bg-[var(--c-film)] rounded-full overflow-hidden mb-4">
        <div className="h-full bg-[var(--c-green)] rounded-full transition-all" style={{ width: `${agenda.total ? Math.round(agenda.done / agenda.total * 100) : 0}%`, boxShadow: '0 0 10px #65A30D88' }} />
      </div>
      <div className="space-y-3">
        {groups.map(g => (
          <div key={g.key}>
            <div className="text-[11px] uppercase tracking-wider font-bold mb-1.5" style={{ color: g.color }}>{g.label}</div>
            <div className="space-y-1">
              {g.items.map((it, i) => (
                <div key={i} className="flex items-center gap-2 text-sm" data-testid={`agenda-${g.key}-${i}`}>
                  {it.done
                    ? <CheckCircle2 className="w-4 h-4 flex-shrink-0" style={{ color: g.color }} />
                    : <Circle className="w-4 h-4 flex-shrink-0 text-muted-foreground/50" />}
                  <span className={it.done ? 'line-through text-muted-foreground' : ''}>{it.name}</span>
                </div>
              ))}
            </div>
          </div>
        ))}
      </div>
    </div>
  );
}


function ScoreUnlockCard({ readiness }) {
  const items = readiness?.items || [];
  const done = readiness?.completed ?? 0;
  const total = readiness?.total ?? items.length ?? 4;
  return (
    <div className="regen-card-elevated p-5 blueprint-grid" data-testid="score-unlock-card">
      <div className="flex items-center justify-between mb-1">
        <div className="label-mini text-[var(--c-green-ink)]">YourReGen Score</div>
        <span className="font-mono text-2xl text-muted-foreground/70">--</span>
      </div>
      <div className="font-display font-black text-lg">Welcome to YourReGen</div>
      <p className="text-sm text-muted-foreground mt-1">Complete these steps to unlock your YourReGen Score. Your score is always based on your real activity — never a placeholder.</p>
      <div className="mt-4 space-y-2.5">
        {items.map((it, i) => (
          <div key={it.key} className="flex items-center gap-2.5" data-testid={`unlock-item-${it.key}`}>
            {it.done
              ? <CheckCircle2 className="w-5 h-5 flex-shrink-0 text-[var(--c-green-ink)]" />
              : <Circle className="w-5 h-5 flex-shrink-0 text-muted-foreground/40" />}
            <span className={`text-sm ${it.done ? 'line-through text-muted-foreground' : 'text-foreground'}`}>{it.label}</span>
          </div>
        ))}
      </div>
      <div className="mt-4 flex items-center gap-3">
        <div className="flex-1 h-2 rounded-full bg-[var(--c-film)] overflow-hidden">
          <div className="h-full rounded-full bg-[var(--c-green)] transition-all" style={{ width: `${(done / total) * 100}%` }} />
        </div>
        <span className="font-mono text-sm text-[var(--c-green-ink)]" data-testid="unlock-progress">{done} / {total}</span>
      </div>
    </div>
  );
}
